import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useSearchStore from "@stores/client/useSearchStore";
import { useSearchTracking } from "@hooks/client/use-search-tracking";
import useUserPreferencesStore from "@stores/shared/useUserPreferencesStore";
import { buildResultUrl, CATEGORY_ICON } from "./search-dialog-constants";

export const ALL_CATEGORIES = Object.keys(CATEGORY_ICON);
export const TABS = ["All", ...ALL_CATEGORIES];

export const useSearchDialog = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  // ref to sense if viewport has reached a point to allow loading more
  const lazyLoadRef = useRef(null);
  // helps with keeping scrolling independent on each tab
  const scrollContainerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const {
    searchData,
    searchRolodex,
    searchError,
    hasMore,
    fetching,
    setSearchQuery,
    setSemanticWeight,
    loadMore,
  } = useSearchStore();
  const semanticLevel = useSearchStore((s) => s.config.semanticWeight);
  const { trackSearch } = useSearchTracking();

  const handleChange = useCallback((event) => {
    const value = event.target.value;
    setQuery(value);
    if (!value.trim()) {
      setHasSearched(false);
      setActiveTab(0);
    }
  }, []);

  const handleClear = useCallback(() => {
    setQuery("");
    setHasSearched(false);
    setActiveTab(0);
  }, []);

  const runSearch = useCallback(async () => {
    if (!query.trim()) return;
    setActiveTab(0);
    setHasSearched(true);
    const start = Date.now();
    await setSearchQuery(query.trim());
    const resultCount =
      useSearchStore.getState().searchRolodex?.totalCount ?? 0;
    trackSearch(query.trim(), resultCount, Date.now() - start);
  }, [query, setSearchQuery, trackSearch]);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  const handleResultClick = useCallback(
    (itemOrUrl) => {
      navigate(typeof itemOrUrl === "string" ? itemOrUrl : buildResultUrl(itemOrUrl));
    },
    [navigate],
  );

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      onClose?.();
    }
  }, [location.pathname, onClose]);

  const results = hasSearched ? (searchData ?? []) : [];

  const categoryCounts = useMemo(() => {
    const counts = Object.fromEntries(ALL_CATEGORIES.map((cat) => [cat, 0]));
    results.forEach((item) => {
      if (item.type in counts) counts[item.type]++;
    });
    return counts;
  }, [results]);

  const activeCategory = TABS[activeTab] === "All" ? null : TABS[activeTab];

  const visibleResults = useMemo(
    () =>
      !activeCategory
        ? results
        : results.filter((item) => item.type === activeCategory),
    [results, activeCategory],
  );

  useEffect(() => {
    const { getPreference } = useUserPreferencesStore.getState();
    getPreference("search-semantic-weight")
      .then((value) => {
        if (value != null) setSemanticWeight(Number(value));
      })
      .catch(() => {});
  }, [setSemanticWeight]);

  useEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [activeTab]);

  useEffect(() => {
    const sentinel = lazyLoadRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !fetching) loadMore();
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, fetching, loadMore]);

  return {
    query,
    hasSearched,
    activeTab,
    setActiveTab,
    semanticLevel,
    results,
    categoryCounts,
    activeCategory,
    visibleResults,
    searchRolodex,
    searchError,
    hasMore,
    fetching,
    lazyLoadRef,
    scrollContainerRef,
    handleChange,
    handleClear,
    handleClose,
    handleResultClick,
    runSearch,
    setSemanticWeight,
    showSuggested: !hasSearched && !fetching,
    showSkeleton: hasSearched && fetching && results.length === 0,
    showError: hasSearched && !fetching && !!searchError,
    showNoResults:
      hasSearched && !fetching && !searchError && results.length === 0,
    showResults: hasSearched && !searchError && results.length > 0,
  };
};
